import React from 'react';
import { FormSection } from '../../../components/FormSection';
import { FormPage } from '../../../components/FormPage';
import { TextField } from 'mui-rff';
import { Autocomplete, Radios } from 'mui-rff';
import { FormCondition } from '../../../components/FormCondition';
import Typography from '@material-ui/core/Typography';
import { PayoutSettingsType } from '../../../types/generated';
import * as Yup from 'yup';

const paymentTypeOptions = [
    { value: 'WIRE', label: 'Wire transfer', us: true, eu: true },
    { value: 'SEPA', label: 'Single Euro Payment (SEPA)', us: false, eu: true },
    { value: 'ACHGIRO', label: 'ACH/Giro transfer', us: false, eu: true },
    { value: 'ACH', label: 'ACH transfer', us: true, eu: false },
];

const defaultInitialValues: PayoutSettingsType = {
    bankAccountNumber: '',
    bankName: '',
    currencyCode: 'USD',
    usResident: false,
    payableTo: '',
    paymentTerms: '',
    paymentType: 'WIRE',
    payoutContactEmail: '',
    payoutContactPhoneNumber: '',
    abaRouting: '',
    bankId: '',
    branchId: '',
    iban: '',
    swift: '',
};

const validationSchema = Yup.object().shape({
    bankAccountNumber: Yup.string().required('You have to enter the bank account number'),
    bankName: Yup.string().required('You have to enter the name of the bank'),
    payableTo: Yup.string().required('You have to enter the name of the payment receiver'),
    payoutContactEmail: Yup.string()
        .email('You have to enter a valid e-mail address')
        .required('You have to enter a valid e-mail address'),
    payoutContactPhoneNumber: Yup.string().required('You have to enter a contact phone number'),
    paymentType: Yup.string().required('Select the type of payment'),
    usResident: Yup.string().required('Please fill in if you live in the states or not'),
    bankId: Yup.string().when('usResident', {
        is: 'false',
        then: Yup.string().required('Fill out the ID number of your bank'),
        otherwise: Yup.string().nullable(),
    }),
    swift: Yup.string().when('usResident', {
        is: 'false',
        then: Yup.string().required('Fill out the swift code from your bank'),
        otherwise: Yup.string().nullable(),
    }),
    iban: Yup.string().when('usResident', {
        is: 'false',
        then: Yup.string().required('Fill out the IBAN number of your bank account'),
        otherwise: Yup.string().nullable(),
    }),
    abaRouting: Yup.string().when('usResident', {
        is: 'true',
        then: Yup.string().required('Fill out the ABA/Routing number of your bank account'),
        otherwise: Yup.string().nullable(),
    }),
});

interface PayoutSettingsViewProps {
    loading: boolean;
    currencyOptions: { code: string; name: string }[];
    initialValues?: PayoutSettingsType;
    onSubmit: (values, form, callback) => void;
    snackbar?: React.ReactNode;
}

export const PayoutSettingsView = ({
    loading,
    currencyOptions,
    initialValues = defaultInitialValues,
    onSubmit,
    snackbar,
}: PayoutSettingsViewProps): JSX.Element => {
    return (
        <FormPage
            title="Payout settings"
            loading={loading}
            initialValues={{
                ...initialValues,
                usResident: initialValues.usResident ? 'true' : 'false',
            }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            render={() => (
                <React.Fragment>
                    <FormSection title="Contact information">
                        <TextField
                            type="text"
                            fullWidth
                            id="payoutContactEmail"
                            name="payoutContactEmail"
                            required
                            label="Contact e-mail"
                            helperText="Whom should we contact via e-mail if we require more details"
                            margin="normal"
                            autoFocus
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                        <TextField
                            type="text"
                            fullWidth
                            id="payoutContactPhoneNumber"
                            name="payoutContactPhoneNumber"
                            required
                            label="Contact phone number"
                            helperText="Whom should we contact via phone if we require more details"
                            margin="normal"
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                    </FormSection>
                    <FormSection title={'Payment settings'}>
                        <TextField
                            type="text"
                            fullWidth
                            id="payableTo"
                            name="payableTo"
                            label="Payable to"
                            helperText="Enter the name that we should put on the invoice"
                            required
                            margin="normal"
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                        <Autocomplete
                            name="currencyCode"
                            label="Preferred payout currency"
                            textFieldProps={{ margin: 'normal' }}
                            options={currencyOptions}
                            getOptionValue={(option: { code: string; name: string }) => option.code}
                            getOptionLabel={(option: { code: string; name: string }) => option.name}
                            required
                        />
                        <TextField
                            type="text"
                            fullWidth
                            id="paymentTerms"
                            name="paymentTerms"
                            label="Payment terms"
                            helperText="Optional: Fill this in if you have any specific payment terms you would like us to review"
                            margin="normal"
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                    </FormSection>
                    <FormSection
                        title={'Are you a US resident?'}
                        description={
                            'You may need to fill in additional information to receive payments if you are within the United States'
                        }
                    >
                        <Radios
                            name="usResident"
                            data={[
                                { value: 'true', label: 'Yes' },
                                { value: 'false', label: 'No' },
                            ]}
                        />
                    </FormSection>
                    <FormSection title={'Bank details'}>
                        <Typography>How would you like to receive the payment?</Typography>
                        <FormCondition when={'usResident'} is={'true'}>
                            <Radios
                                name="paymentType"
                                data={paymentTypeOptions.filter(pt => pt.us)}
                            />
                        </FormCondition>
                        <FormCondition when={'usResident'} is={'false'}>
                            <Radios
                                name="paymentType"
                                data={paymentTypeOptions.filter(pt => pt.eu)}
                            />
                        </FormCondition>
                        <TextField
                            type="text"
                            fullWidth
                            id="bankName"
                            name="bankName"
                            label="Bank name"
                            helperText="What is the name of the bank we should pay to?"
                            margin="normal"
                            required
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                        <FormCondition when={'usResident'} is={'false'}>
                            <TextField
                                type="text"
                                fullWidth
                                id="bankId"
                                name="bankId"
                                label="Bank Id"
                                helperText={'The bank ID'}
                                margin="normal"
                                required
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                            <TextField
                                type="text"
                                fullWidth
                                id="branchId"
                                name="branchId"
                                label="Branch Id"
                                helperText={'The branch ID'}
                                margin="normal"
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                            <TextField
                                type="text"
                                fullWidth
                                id="swift"
                                name="swift"
                                label="SWIFT"
                                helperText="SWIFT account number"
                                required
                                margin="normal"
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                            <TextField
                                type="text"
                                fullWidth
                                id="iban"
                                name="iban"
                                label="IBAN"
                                helperText="IBAN number"
                                required
                                margin="normal"
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                        </FormCondition>
                        <FormCondition when={'usResident'} is={'true'}>
                            <TextField
                                type="text"
                                fullWidth
                                id="abaRouting"
                                name="abaRouting"
                                label="ABA/Routing #"
                                helperText="ABA/Routing number"
                                required
                                margin="normal"
                                inputProps={{
                                    maxLength: 255,
                                }}
                            />
                        </FormCondition>
                        <TextField
                            type="text"
                            fullWidth
                            id="bankAccountNumber"
                            name="bankAccountNumber"
                            label="Bank account number"
                            helperText="The bank account number"
                            margin="normal"
                            required
                            inputProps={{
                                maxLength: 255,
                            }}
                        />
                    </FormSection>
                    {snackbar}
                </React.Fragment>
            )}
        />
    );
};
